import { useQuery } from "@apollo/react-hooks";
import { Box, Grid, useMediaQuery } from "@material-ui/core";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router-dom";

import { ReactComponent as ChevronLeft } from "assets/chevron-left.svg";
import { Button, Loading, Notifier } from "components/simple";
import {
  GetCoachingSession,
  GetZoomTokenRequest
} from "graphql/sessions/sessions.gql";
import apolloClient from "graphql/client";
import { breakpoints, spacingDefaults } from "style/constants";
import { addMinutes } from "date-fns";
import routesConfig from "utils/routesConfig";
import { getUser } from "state/selectors";
import {
  CoachVideoSession,
  SDKConfig
} from "@brainhealth/video-sessions-sdk/dist/index.js";
import { ZoomGraphQLCredentialProvider } from "@brainhealth/video-sessions-sdk/dist/index.js";
import "@brainhealth/video-sessions-sdk/dist/index.css";
import { RecordingConsentScreen } from "lib/zoom-ui/RecordingConsentScreen";

interface RouteProps {
  coachingSessionId: string;
}

type CoachingSession = {
  id: string;
  participant: {
    id: string;
    firstName: string;
    lastName: string;
    profileImageKey: string;
    showFirstVideoCallInfo: boolean;
  };
  sessionTime: Date;
  durationMinutes: number;
};

const CoachingCall: React.FC<RouteComponentProps<RouteProps>> = ({
  match
}: RouteComponentProps<RouteProps>) => {
  const { coachingSessionId } = match.params;

  const dispatch = useDispatch();
  const user = useSelector(getUser);

  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);

  const [coachingSession, setCoachingSession] = useState<CoachingSession>();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [consentCompleted, setConsentCompleted] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);

  const { loading } = useQuery(GetCoachingSession, {
    variables: { id: coachingSessionId },

    fetchPolicy: "no-cache",

    onCompleted(data) {
      setCoachingSession(data.coachingSessionNC);
    },

    onError() {
      dispatch({ type: "server_error", value: true });
    }
  });

  const history = useHistory();
  useEffect(() => {
    if (coachingSession) {
      const handle = setInterval(() => {
        const sessionEndTime = addMinutes(
          new Date(new Date(coachingSession?.sessionTime)),
          coachingSession?.durationMinutes
        );
        setTimeRemaining(
          Math.floor(new Date(sessionEndTime).getTime() / 1000) -
            Math.floor(new Date().getTime() / 1000)
        );
      }, 1000);

      return () => clearInterval(handle);
    }
  }, [coachingSession]);
  const showTimeRemaining = Math.floor(timeRemaining / 60) < 5;
  const zeroPad = Math.abs(timeRemaining % 60) < 10;
  const handleBackToDashboard = () => {
    history.push(routesConfig.dashboard.path);
  };

  // Create SDK config with ZoomGraphQLCredentialProvider
  const sdkConfig: SDKConfig = useMemo(() => {
    const credentialProvider = new ZoomGraphQLCredentialProvider({
      apolloClient,
      getZoomTokenQuery: GetZoomTokenRequest,
      getUserId: () => {
        return user?.id;
      },
      sessionNamePrefix: process.env.REACT_APP_ZOOM_SESSION_NAME
    });

    return {
      provider: "zoom",
      credentialsProvider: credentialProvider,
      debug: true,
      ui: {
        mobileBreakpoint: 900
      }
    };
  }, [user?.id, coachingSessionId]);

  if (loading) return <Loading />;
  if (!coachingSession) return <></>;
  const displayName = `${coachingSession?.participant?.firstName} ${coachingSession?.participant?.lastName}`;

  return (
    <Box
      padding={
        mobile
          ? `${spacingDefaults.normal} ${spacingDefaults.normal} ${spacingDefaults.normal} ${spacingDefaults.normal}`
          : `${spacingDefaults.large} ${spacingDefaults.large} ${spacingDefaults.large} ${spacingDefaults.large}`
      }
      display="flex"
      flexDirection="column"
    >
      {!mobile && (
        <Box marginBottom={spacingDefaults.large}>
          <Button onClick={() => history.push(`/dashboard`)} plain>
            <Grid container alignItems="center">
              <ChevronLeft />
              <Box marginLeft={spacingDefaults.normal} paddingTop="1px">
                Back to Dashboard
              </Box>
            </Grid>
          </Button>
        </Box>
      )}
      <Notifier
        hideNotification={() => {}}
        open={showTimeRemaining && coachingSession?.durationMinutes != null}
        status="dismissed"
        title={
          "Your coaching session is scheduled to end in " +
          (Math.floor(timeRemaining / 60) +
            ":" +
            (zeroPad
              ? "0" + Math.abs(timeRemaining % 60)
              : Math.abs(timeRemaining % 60)))
        }
        forDrawer={false}
      />
      <Box flex={1} minHeight={mobile ? "auto" : "80vh"} width="100%">
        {!consentCompleted ? (
          <RecordingConsentScreen
            onConsent={allowed => {
              setRecordingConsent(allowed);
              setConsentCompleted(true);
            }}
          />
        ) : (
          <CoachVideoSession
            config={sdkConfig}
            defaultSessionName={coachingSessionId}
            defaultUserName={displayName}
            recordingConsent={recordingConsent}
            userRole="participant"
            sessionTime={new Date(
              coachingSession.sessionTime
            ).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              timeZoneName: "short"
            })}
            onBackToDashboard={handleBackToDashboard}
          />
        )}
      </Box>
    </Box>
  );
};

export default CoachingCall;
